import { useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ApiClientError } from '../api/http'
import { Button, Card, Icon, I, MOBILE_SINGLE_COLUMN, Pill, TEXT_WRAP_STYLE, mobileButtonStyle } from '../components/ui'
import { useIsMobileViewport } from '../hooks/useIsMobileViewport'
import { getZohoOrderAudit } from '../inventory/inventoryApi'
import type {
  ZohoOrderAuditClassification,
  ZohoOrderAuditDifference,
  ZohoOrderAuditDocument,
  ZohoOrderAuditItem,
  ZohoOrderAuditResponse,
} from '../inventory/types'

interface InventoryZohoOrderAuditPageProps {
  accessToken: string
  inventoryConnectionId: number | null
  onBack: () => void
}

type Translate = (key: string, options?: Record<string, unknown>) => string

const DAY_OPTIONS = [2, 7, 30, 90]
const CLASSIFICATIONS: ZohoOrderAuditClassification[] = [
  'OK',
  'MISSING_SHIPPING_SERVICE_LINE',
  'WRONG_SHIPPING_SERVICE_AMOUNT',
  'WRONG_ENTITY_DISCOUNT',
  'TOTAL_MISMATCH',
  'ZOHO_DOCUMENT_NOT_FOUND',
  'UNSAFE_MANUAL_DIFFERENCE',
]

function classificationTone(classification: ZohoOrderAuditClassification): 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'brand' | 'coral' {
  if (classification === 'OK') return 'success'
  if (classification === 'UNSAFE_MANUAL_DIFFERENCE' || classification === 'ZOHO_DOCUMENT_NOT_FOUND') return 'danger'
  if (classification === 'TOTAL_MISMATCH') return 'coral'
  return 'warning'
}

function errorKey(error: unknown): string {
  if (error instanceof ApiClientError) return `pages.inventoryZohoOrderAudit.errors.${error.code}`
  return 'pages.inventoryZohoOrderAudit.errors.REQUEST_FAILED'
}

function formatDateTime(value: string | null | undefined, locale: string, fallback: string) {
  if (!value) return fallback
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(date)
}

function statCount(audit: ZohoOrderAuditResponse | null, classification: ZohoOrderAuditClassification) {
  return audit?.summary.byClassification[classification] ?? 0
}

function mergeAuditPage(previous: ZohoOrderAuditResponse | null, next: ZohoOrderAuditResponse): ZohoOrderAuditResponse {
  if (!previous) return next
  const byClassification: Partial<Record<ZohoOrderAuditClassification, number>> = { ...previous.summary.byClassification }
  for (const [classification, count] of Object.entries(next.summary.byClassification) as Array<[ZohoOrderAuditClassification, number]>) {
    byClassification[classification] = (byClassification[classification] ?? 0) + count
  }
  return {
    ...next,
    scannedCount: previous.scannedCount + next.scannedCount,
    summary: {
      total: previous.summary.total + next.summary.total,
      byClassification,
    },
    items: [...previous.items, ...next.items],
  }
}

function MissingValue({ value, fallback }: { value?: string | number | null; fallback: string }) {
  const text = value == null || value === '' ? fallback : String(value)
  const missing = value == null || value === ''
  return (
    <span style={{ fontFamily: missing ? 'inherit' : 'var(--font-mono)', color: missing ? 'var(--fg-subtle)' : 'var(--fg)', ...TEXT_WRAP_STYLE }}>
      {text}
    </span>
  )
}

function DifferenceList({ differences, fallback, t }: { differences: ZohoOrderAuditDifference[]; fallback: string; t: Translate }) {
  if (differences.length === 0) return <div style={{ color: 'var(--fg-subtle)', fontSize: 12.5 }}>{fallback}</div>
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {differences.map((difference, index) => (
        <div key={`${difference.documentType}-${difference.code}-${difference.field}-${index}`} style={{ display: 'grid', gap: 6, padding: 10, borderRadius: 12, background: 'var(--ink-50)', border: '1px solid var(--border-subtle)' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--fg-subtle)' }}>{difference.code}</span>
            <span style={{ fontWeight: 800, color: 'var(--fg)', fontSize: 12.5, ...TEXT_WRAP_STYLE }}>{difference.field}</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 8, color: 'var(--fg-muted)', fontSize: 12 }}>
            <div><strong style={{ color: 'var(--fg-subtle)' }}>{t('pages.inventoryZohoOrderAudit.differences.expected')}</strong> <MissingValue value={difference.expected} fallback={String(t('pages.inventoryZohoOrderAudit.notAvailable'))} /></div>
            <div><strong style={{ color: 'var(--fg-subtle)' }}>{t('pages.inventoryZohoOrderAudit.differences.actual')}</strong> <MissingValue value={difference.actual} fallback={String(t('pages.inventoryZohoOrderAudit.notAvailable'))} /></div>
            <div><strong style={{ color: 'var(--fg-subtle)' }}>{t('pages.inventoryZohoOrderAudit.differences.delta')}</strong> <MissingValue value={difference.delta} fallback={String(t('pages.inventoryZohoOrderAudit.notAvailable'))} /></div>
          </div>
        </div>
      ))}
    </div>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ color: 'var(--fg-subtle)', fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ marginTop: 4, color: 'var(--fg)', fontSize: 13 }}>{children}</div>
    </div>
  )
}

function DocumentCard({ document, t }: { document: ZohoOrderAuditDocument; t: Translate }) {
  return (
    <div style={{ padding: 12, borderRadius: 14, border: '1px solid var(--border-subtle)', display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 13.5, fontWeight: 900, color: 'var(--brand-900)' }}>{t(`pages.inventoryZohoOrderAudit.documentTypes.${document.type}`)}</div>
          <div style={{ marginTop: 2, fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--fg-subtle)' }}>
            {document.zohoId || t('pages.inventoryZohoOrderAudit.notAvailable')}
          </div>
        </div>
        <Pill tone={classificationTone(document.classification)} dot>{t(`pages.inventoryZohoOrderAudit.classifications.${document.classification}`)}</Pill>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
        <Field label={String(t('pages.inventoryZohoOrderAudit.documents.expected'))}>{document.expected ? t('pages.inventoryZohoOrderAudit.yes') : t('pages.inventoryZohoOrderAudit.no')}</Field>
        <Field label={String(t('pages.inventoryZohoOrderAudit.documents.found'))}>{document.found ? t('pages.inventoryZohoOrderAudit.yes') : t('pages.inventoryZohoOrderAudit.no')}</Field>
        <Field label={String(t('pages.inventoryZohoOrderAudit.documents.remoteStatus'))}><MissingValue value={document.remoteStatus} fallback={String(t('pages.inventoryZohoOrderAudit.notAvailable'))} /></Field>
      </div>
      <DifferenceList differences={document.differences} fallback={String(t('pages.inventoryZohoOrderAudit.differences.none'))} t={t} />
    </div>
  )
}

function OrderCard({ item, locale, t }: { item: ZohoOrderAuditItem; locale: string; t: Translate }) {
  const fallback = String(t('pages.inventoryZohoOrderAudit.notAvailable'))
  const title = item.externalOrderNumber || item.externalOrderId || t('pages.inventoryZohoOrderAudit.orderNumber', { id: item.orderId })
  return (
    <Card padding={18}>
      <div style={{ display: 'grid', gap: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 900, color: 'var(--brand-900)', fontSize: 15, ...TEXT_WRAP_STYLE }}>{title}</div>
            <div style={{ marginTop: 3, fontFamily: 'var(--font-mono)', color: 'var(--fg-subtle)', fontSize: 11.5 }}>{t('pages.inventoryZohoOrderAudit.orderId', { id: item.orderId })}</div>
          </div>
          <Pill tone={classificationTone(item.classification)} dot>{t(`pages.inventoryZohoOrderAudit.classifications.${item.classification}`)}</Pill>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
          <Field label={String(t('pages.inventoryZohoOrderAudit.fields.salesOrder'))}><MissingValue value={item.zohoSalesOrderId} fallback={fallback} /></Field>
          <Field label={String(t('pages.inventoryZohoOrderAudit.fields.invoice'))}><MissingValue value={item.zohoInvoiceId} fallback={fallback} /></Field>
          <Field label={String(t('pages.inventoryZohoOrderAudit.fields.syncedAt'))}>{formatDateTime(item.zohoSyncedAt, locale, fallback)}</Field>
          <Field label={String(t('pages.inventoryZohoOrderAudit.fields.policy'))}>{item.policyId ? t('pages.inventoryZohoOrderAudit.policyVersion', { id: item.policyId, version: item.policyVersion ?? fallback }) : fallback}</Field>
        </div>
        {(item.candidateReasons.length > 0 || item.blockingErrors.length > 0) && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {item.candidateReasons.map(reason => <Pill key={reason} tone="info">{reason}</Pill>)}
            {item.blockingErrors.map(error => <Pill key={error.code} tone="danger">{error.code}</Pill>)}
          </div>
        )}
        <div style={{ display: 'grid', gap: 10 }}>
          {item.documents.map(document => <DocumentCard key={`${document.type}-${document.zohoId ?? 'missing'}`} document={document} t={t} />)}
        </div>
        {item.differences.length > 0 && (
          <div style={{ display: 'grid', gap: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 900, color: 'var(--fg)' }}>{t('pages.inventoryZohoOrderAudit.differences.title')}</div>
            <DifferenceList differences={item.differences} fallback={String(t('pages.inventoryZohoOrderAudit.differences.none'))} t={t} />
          </div>
        )}
      </div>
    </Card>
  )
}

export function InventoryZohoOrderAuditPage({ accessToken, inventoryConnectionId, onBack }: InventoryZohoOrderAuditPageProps) {
  const { t, i18n } = useTranslation()
  const isMobile = useIsMobileViewport()
  const [days, setDays] = useState(2)
  const [limit, setLimit] = useState(50)
  const [audit, setAudit] = useState<ZohoOrderAuditResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const translate: Translate = (key, options) => String(t(key, options))
  const runAudit = (nextOffset = 0, append = false) => {
    if (inventoryConnectionId == null) return
    setLoading(true)
    setError(null)
    getZohoOrderAudit(accessToken, inventoryConnectionId, { days, limit, offset: nextOffset })
      .then(next => setAudit(previous => append ? mergeAuditPage(previous, next) : next))
      .catch(caught => {
        if (!append) setAudit(null)
        setError(caught)
      })
      .finally(() => setLoading(false))
  }

  const items = audit?.items ?? []
  const generatedAt = useMemo(() => formatDateTime(audit?.generatedAt, i18n.language, String(t('pages.inventoryZohoOrderAudit.notAvailable'))), [audit?.generatedAt, i18n.language, t])
  const since = useMemo(() => formatDateTime(audit?.since, i18n.language, String(t('pages.inventoryZohoOrderAudit.notAvailable'))), [audit?.since, i18n.language, t])

  if (inventoryConnectionId == null) {
    return (
      <div style={{ display: 'grid', gap: 16 }}>
        <Button kind="ghost" size="sm" icon={I.arrowL} onClick={onBack}>{t('pages.inventoryZohoOrderAudit.back')}</Button>
        <Card padding={24} style={{ borderStyle: 'dashed', background: 'transparent' }}>
          <div style={{ fontWeight: 900, color: 'var(--fg)' }}>{t('pages.inventoryZohoOrderAudit.missing.title')}</div>
          <div style={{ marginTop: 6, color: 'var(--fg-muted)', fontSize: 13, lineHeight: 1.5 }}>{t('pages.inventoryZohoOrderAudit.missing.body')}</div>
        </Card>
      </div>
    )
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Button kind="ghost" size="sm" icon={I.arrowL} onClick={onBack} style={mobileButtonStyle(isMobile)}>{t('pages.inventoryZohoOrderAudit.back')}</Button>
        <Pill tone="info" dot>{t('pages.inventoryZohoOrderAudit.readOnly')}</Pill>
      </div>

      <Card padding={isMobile ? 18 : 22}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? MOBILE_SINGLE_COLUMN : 'minmax(0, 1fr) auto', gap: 16, alignItems: 'start' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <Icon size={20} color="var(--brand-500)">{I.shield}</Icon>
              <h2 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: 'var(--brand-900)', letterSpacing: '-0.01em' }}>{t('pages.inventoryZohoOrderAudit.title')}</h2>
            </div>
            <div style={{ marginTop: 6, color: 'var(--fg-muted)', fontSize: 13.5, lineHeight: 1.55, maxWidth: 720 }}>
              {t('pages.inventoryZohoOrderAudit.subtitle')}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? MOBILE_SINGLE_COLUMN : 'auto auto auto', gap: 8, alignItems: 'end' }}>
            <label style={{ display: 'grid', gap: 4, fontSize: 12, color: 'var(--fg-subtle)', fontWeight: 800 }}>
              {t('pages.inventoryZohoOrderAudit.filters.days')}
              <select value={days} onChange={event => setDays(Number(event.target.value))} style={{ border: '1px solid var(--border-default)', borderRadius: 10, padding: '8px 10px', font: 'inherit', color: 'var(--fg)', background: 'white' }}>
                {DAY_OPTIONS.map(option => <option key={option} value={option}>{t('pages.inventoryZohoOrderAudit.filters.daysOption', { count: option })}</option>)}
              </select>
            </label>
            <label style={{ display: 'grid', gap: 4, fontSize: 12, color: 'var(--fg-subtle)', fontWeight: 800 }}>
              {t('pages.inventoryZohoOrderAudit.filters.limit')}
              <input type="number" min={1} value={limit} onChange={event => setLimit(Math.max(1, Number(event.target.value) || 1))} style={{ border: '1px solid var(--border-default)', borderRadius: 10, padding: '8px 10px', font: 'inherit', color: 'var(--fg)', background: 'white', width: 110 }} />
            </label>
            <Button kind="primary" size="sm" icon={I.refresh} onClick={() => runAudit()} disabled={loading} style={mobileButtonStyle(isMobile)}>{loading ? t('pages.inventoryZohoOrderAudit.loadingShort') : t('pages.inventoryZohoOrderAudit.actions.fetchLatest')}</Button>
          </div>
        </div>
      </Card>

      {error != null && (
        <Card padding={14} style={{ borderColor: 'var(--danger-100)', background: 'var(--danger-100)' }}>
          <div style={{ color: 'var(--danger)', fontWeight: 800, fontSize: 13 }}>
            {String(t(errorKey(error), { defaultValue: String(t('pages.inventoryZohoOrderAudit.errors.REQUEST_FAILED')) }))}
          </div>
        </Card>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(145px, 1fr))', gap: 12 }}>
        <Card padding={16} style={{ background: 'var(--ink-50)' }}>
          <div style={{ color: 'var(--fg-subtle)', fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{t('pages.inventoryZohoOrderAudit.summary.scanned')}</div>
          <div style={{ marginTop: 6, color: 'var(--brand-900)', fontSize: 24, fontWeight: 900 }}>{loading && !audit ? t('pages.inventoryZohoOrderAudit.loadingShort') : audit?.scannedCount ?? 0}</div>
          <div style={{ marginTop: 4, color: 'var(--fg-muted)', fontSize: 12 }}>{t('pages.inventoryZohoOrderAudit.summary.since', { since })}</div>
        </Card>
        {CLASSIFICATIONS.map(classification => (
          <Card key={classification} padding={16} style={{ background: classification === 'OK' ? 'var(--success-100)' : 'white' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
              <div style={{ color: 'var(--fg-subtle)', fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', ...TEXT_WRAP_STYLE }}>{t(`pages.inventoryZohoOrderAudit.classifications.${classification}`)}</div>
              <Pill tone={classificationTone(classification)} size="sm">{statCount(audit, classification)}</Pill>
            </div>
          </Card>
        ))}
      </div>

      <Card padding={0}>
        <div style={{ padding: 18, borderBottom: '1px solid var(--border-subtle)', display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontWeight: 900, color: 'var(--fg)', fontSize: 15 }}>{t('pages.inventoryZohoOrderAudit.results.title')}</div>
            <div style={{ marginTop: 3, color: 'var(--fg-muted)', fontSize: 12.5 }}>{t('pages.inventoryZohoOrderAudit.results.subtitle', { generatedAt })}</div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {audit?.hasMore && audit.nextOffset != null && <Button kind="secondary" size="sm" icon={I.refresh} onClick={() => runAudit(audit.nextOffset ?? 0, true)} disabled={loading}>{t('pages.inventoryZohoOrderAudit.actions.fetchNextPage')}</Button>}
            {loading && <Pill tone="info">{t('pages.inventoryZohoOrderAudit.loading')}</Pill>}
          </div>
        </div>

        {loading && !audit ? (
          <div style={{ padding: 28, color: 'var(--fg-muted)', fontSize: 13, fontWeight: 800 }}>{t('pages.inventoryZohoOrderAudit.loading')}</div>
        ) : !audit && error == null ? (
          <div style={{ padding: 28, textAlign: 'center', color: 'var(--fg-muted)', fontSize: 13, lineHeight: 1.55 }}>
            <div style={{ fontWeight: 900, color: 'var(--fg)', marginBottom: 4 }}>{t('pages.inventoryZohoOrderAudit.notRun.title')}</div>
            <div>{t('pages.inventoryZohoOrderAudit.notRun.body')}</div>
          </div>
        ) : items.length === 0 ? (
          <div style={{ padding: 28, textAlign: 'center', color: 'var(--fg-muted)', fontSize: 13, lineHeight: 1.55 }}>
            <div style={{ fontWeight: 900, color: 'var(--fg)', marginBottom: 4 }}>{t('pages.inventoryZohoOrderAudit.empty.title')}</div>
            <div>{t('pages.inventoryZohoOrderAudit.empty.body')}</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 12, padding: 14 }}>
            {items.map(item => <OrderCard key={item.orderId} item={item} locale={i18n.language} t={translate} />)}
          </div>
        )}
      </Card>
    </div>
  )
}
